import { Component, Prop, h } from '@stencil/core';
import { getCanPostAnswer } from '@ugc-api/question';
import { IQuestionInfo } from '@ugc-components/ugc-pdp-question-and-answer/interfaces';
import state from '@ugc-store';
import { FontType, handleErrorMessage, showToast, login } from '@ugc-utils/utils';
import { ReportAbuseType } from '@ugc-containers/ugc-report-abuse/interfaces';
import { modalZIndex } from '@ugc-utils/modal-z-index';

@Component({
  tag: 'ugc-question-title',
  styleUrl: 'ugc-question-title.scss',
  shadow: false,
})
export class UgcQuestionTitle {
  @Prop() isModal?: boolean = false;
  @Prop() questionInfo: Partial<IQuestionInfo> = {};
  loading = false;

  handleAnswerClick = async (isAnswered: boolean) => {
    if (this.loading) return;

    const {
      actionPermission: { answer: answerButtonDisabled },
      isLogin,
      productInfo: { productId },
      domains: { catalog },
    } = state;

    if (isAnswered) {
      window.location.href = `${catalog}/ugc/myaccount/answers`;
      return;
    }

    if (!isLogin) {
      login();
      return;
    }

    if (answerButtonDisabled) return;

    try {
      this.loading = true;
      const { businessViolations = [] } = (await getCanPostAnswer({ pid: productId, qid: this.questionInfo.id })) || {};

      this.loading = false;
      if (businessViolations.length) {
        const { message = '' } = businessViolations[0] || {};
        showToast({
          type: 'warn',
          message: message,
        });
      } else {
        state.isShowAnswerModal = true;
        state.answerModalData = {
          questionId: this.questionInfo.id,
        };
      }
    } catch (e) {
      this.loading = false;
      handleErrorMessage(e);
    }
  };

  render() {
    const {
      qnaStaticLanguage: {
        IDS_UGC_QNA_ANSWER = '',
        IDS_MSG_SHOW_MORE = '',
        IDS_MSG_SHOW_LESS = '',
        IDS_BTN_EDIT_ANSWER = '',
      },
      actionPermission: { answer: answerButtonDisabled },
      isDesktop,
      answeredList,
    } = state;

    const { id: qid, content, questionShareUrl } = this.questionInfo;
    const answered = answeredList.find(({ questionId }) => questionId === qid);
    const isAnswered = answered ? answered.isAnswered : false;

    const isLargeType = isDesktop && !this.isModal;
    const fontType = isLargeType ? FontType.large : FontType.small;

    return (
      <div class={`${isLargeType ? 'ugc-question-title-wrap' : 'flex-col-start-start'} mb-16 w-full`}>
        <div class={`${isLargeType && 'question-text'} `}>
          <ugc-ellipsis-text
            contentStyle={{ fontWeight: 'bold', lineHeight: '24px' }}
            buttonStyle={{ fontSize: isLargeType ? '16px' : '14px' }}
            iconSize={isLargeType ? '24' : '20'}
            line={isLargeType ? 3 : 6}
            text={content}
            readMore={IDS_MSG_SHOW_MORE}
            readLess={IDS_MSG_SHOW_LESS}
          />
        </div>
        <div
          class={`${
            isLargeType ? 'question-button flex-row-reverse-start-center' : 'flex-row-between-center mt-8'
          } gap-20 w-full`}
        >
          <ugc-button
            height="32"
            type="ghost"
            fontWeight="400"
            disabled={answerButtonDisabled}
            onClick={() => {
              this.handleAnswerClick(isAnswered);
            }}
            data-testid="pdp-qna-answer-button"
            style={{
              'flex-shrink': '0',
            }}
          >
            {isAnswered ? IDS_BTN_EDIT_ANSWER : IDS_UGC_QNA_ANSWER}
          </ugc-button>
          <div class={`flex-row-start-center ${isLargeType && 'gap-14'}`}>
            <ugc-report-abuse
              type={ReportAbuseType.Question}
              referenceId={qid}
              fontType={fontType}
              data-testid="pdp-qna-question-report-abuse"
            />
            <div class="lh-16">
              <ugc-share
                shareUrl={questionShareUrl}
                shareWidgetLayer={this.isModal && modalZIndex['share-widget-layer']}
                sid={`${isDesktop}-${this.isModal}-${qid}`}
                gaEvent={{
                  'data-ga-event-name': 'open_share_widget',
                  'data-ga-content-type': 'qna',
                  'data-ga-location-detail': 'pdp qna',
                }}
                fontType={fontType}
                data-testid="pdp-qna-question-share"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
